import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import IconWithFallback from "Global/IconWithFallback";
import React from "react";
import { ICON_TYPES } from "@shared/common/constants";
import { SubGroupSupportedExchanges } from "@shared/types";
import Flex from "common/Boxes/Flex";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { useSupportedExchanges } from "@shared/features/newMarketplace/hooks/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled, { useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface SupportedExchangesProps {
  supportedExchanges: SubGroupSupportedExchanges;
}

const SupportedExchanges = ({
  supportedExchanges,
}: SupportedExchangesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const exchanges = useSupportedExchanges(supportedExchanges);
  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <NewLookList>
        {exchanges.map((exchange) => (
          <NewLookRow key={exchange.exchange}>
            <IconWithFallback
              coinOrExchangeName={exchange.exchange}
              iconType={ICON_TYPES.EXCHANGE}
              iconStyle={{ height: 24, width: 24 }}
            />
            <NewLookName>{exchange.exchange}</NewLookName>
            <NewLookCount>
              {t("overview.signals", { count: exchange.number_of_signals })}
            </NewLookCount>
          </NewLookRow>
        ))}
      </NewLookList>
    );
  }

  return (
    <Row gutter={[24, 24]}>
      {exchanges.map((exchange, index) => (
        <Col span={isMobile ? 12 : 8} key={index}>
          <Flex
            $justifyContent="flex-start"
            $alignItems={isMobile ? "flex-start" : "center"}
          >
            <IconWithFallback
              coinOrExchangeName={exchange.exchange}
              iconType={ICON_TYPES.EXCHANGE}
              iconStyle={{
                height: isMobile ? 28 : 32,
                width: isMobile ? 28 : 32,
              }}
            />
            <div style={{ marginLeft: 8 }}>
              <Title level={4}>{exchange.exchange}</Title>
              <Text type="secondary">
                {t("overview.signals", { count: exchange.number_of_signals })}
              </Text>
            </div>
          </Flex>
        </Col>
      ))}
    </Row>
  );
};

export default SupportedExchanges;

const NewLookList = styled.div`
  display: flex;
  flex-direction: column;
`;
const NewLookRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 8px 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookCount = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
